import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { AppointmentLockDto, BASE_URL_TOKEN } from '@raptor-fe/core';

/**
 * Result of attempting to acquire an appointment lock.
 * - `acquired=true` indicates the caller holds the lock.
 * - `acquired=false` indicates another user holds a live lock; `lock` names that holder.
 */
export interface AcquireResult {
    acquired: boolean;
    lock: AppointmentLockDto;
}

/**
 * API service for managing exclusive locks on appointments during the marlin-mobile check-in flow.
 * One user per appointment at a time, with a server-side 3-minute idle TTL refreshed via heartbeat.
 */
@Injectable({
    providedIn: 'root',
})
export class AppointmentLockApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = inject(BASE_URL_TOKEN);

    /**
     * Attempts to acquire a lock on the appointment for the current user.
     * Returns `acquired=true` on HTTP 200; `acquired=false` with the live holder on HTTP 409.
     * Other errors propagate.
     */
    acquire(tenantId: string, branchId: string, appointmentId: string): Observable<AcquireResult> {
        return this.http.post<AppointmentLockDto>(this.getUrl(tenantId, branchId, appointmentId), null).pipe(
            map((lock) => ({ acquired: true, lock })),
            catchError((err) => {
                if (err?.status === 409 && err?.error) {
                    return of<AcquireResult>({ acquired: false, lock: err.error });
                }
                return throwError(() => err);
            }),
        );
    }

    /**
     * Extends the caller's lock. Returns `true` on HTTP 200 (lock is live and owned by caller);
     * `false` on HTTP 404 (no lock, expired, or held by another user). Other errors propagate.
     */
    heartbeat(tenantId: string, branchId: string, appointmentId: string): Observable<boolean> {
        return this.http.post<void>(`${this.getUrl(tenantId, branchId, appointmentId)}/heartbeat`, null).pipe(
            map(() => true),
            catchError((err) => {
                if (err?.status === 404) {
                    return of(false);
                }
                return throwError(() => err);
            }),
        );
    }

    /**
     * Releases the caller's lock. Idempotent server-side; this method always resolves on 204.
     */
    release(tenantId: string, branchId: string, appointmentId: string): Observable<void> {
        return this.http.delete<void>(this.getUrl(tenantId, branchId, appointmentId));
    }

    private getUrl(tenantId: string, branchId: string, appointmentId: string): string {
        return `${this.baseUrl}v1/tenant/${tenantId}/branch/${branchId}/appointment-lock/${appointmentId}`;
    }
}
